import {
    type TextStyle,
    type ViewStyle,
    StyleSheet,
    Platform
} from "react-native";
import type {
    CodeViewerLineStateStyleConstantType,
    CodeViewerActionsInsetConstantType,
    CodeCopyButtonMeasuresConstantType,
    CodeViewerTitleStyleConstantType,
    CodeActionsHitMarginConstantType,
    CodeViewerBandStyleConstantType,
    CodeCopyStatusDelayConstantType,
    CodeGutterMeasuresConstantType,
    CodeLanguageLabelConstantType,
    CodeTokenColorConstantType,
    CodeViewerDynamicStyleType,
    CodeTextStyleConstantType,
    CodeCopyButtonMeasures,
    CodeGutterMeasures
} from "./type";
import {
    CODE_LANGUAGE_LABELS,
    CODE_FONT_FAMILIES,
    CODE_MONO_ADVANCE,
    CODE_TOKEN_TONES
} from "./constant";
import type {
    Mutable
} from "../../types";
import {
    type ConcentricInsetType,
    concentric
} from "../../helpers/theme/concentric";
import {
    getCornersStyle,
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    getFooterBandStyle
} from "../../helpers/theme/footerBand";
import {
    getHitArea
} from "../../helpers/theme/control";
import {
    getOpaqueColor,
    webStyle
} from "../../utils";

export const CODE_COPY_STATUS_DURATION: [keyof NCoreUIKit.Durations, number] = [
    "medium",
    6
];

export const getCodeFontFamily = ({
    typography
}: CodeTextStyleConstantType): string => {
    if(Platform.OS !== "web") {
        return typography.monoMediumSize.fontFamily;
    }

    return `"${typography.monoMediumSize.fontFamily}", ${Platform.select(CODE_FONT_FAMILIES)}`;
};

export const getCodeTextStyle = ({
    size = "medium",
    typography
}: CodeTextStyleConstantType): TextStyle => {
    const sizeStyle = size === "small" ? {
        lineHeight: typography.bodySmallSize.lineHeight,
        fontSize: typography.bodySmallSize.fontSize
    } : {};

    return {
        ...typography.monoMediumSize,
        ...sizeStyle,
        fontFamily: getCodeFontFamily({
            typography
        })
    } as TextStyle;
};

export const getCodeTokenColor = ({
    colors,
    type
}: CodeTokenColorConstantType): string => {
    return colors.code[CODE_TOKEN_TONES[type] ?? "default"];
};

export const getCodeGutterMeasures = ({
    isSignVisible = false,
    isLineNumberVisible,
    size = "medium",
    isCollapsible,
    iconSizes,
    spaces
}: CodeGutterMeasuresConstantType): CodeGutterMeasures => {
    const isGutterVisible = isLineNumberVisible || isCollapsible || isSignVisible;

    return {
        numberWidth: size === "small" ? spaces.spacingXl + spaces.spacingXs : spaces.spacingXl + spaces.spacingSm,
        codePaddingStart: isGutterVisible ? spaces.spacingXs : spaces.spacingMd,
        codePaddingEnd: spaces.spacingMd,
        signWidth: iconSizes.medium,
        foldWidth: iconSizes.medium
    };
};

export const getCodeLanguageLabel = ({
    languageLabel,
    language
}: CodeLanguageLabelConstantType): string | undefined => {
    if(languageLabel) {
        return languageLabel;
    }

    if(!language) {
        return undefined;
    }

    return CODE_LANGUAGE_LABELS[language.toLowerCase()] ?? language;
};

export const getCodeActionsHitMargin = ({
    controlHeights,
    touchTargets,
    pointerType
}: CodeActionsHitMarginConstantType): number => {
    const hitArea = getHitArea({
        visualSize: controlHeights.small,
        touchTargets,
        pointerType
    });

    return Math.min(0, (controlHeights.small - hitArea.minHeight) / 2);
};

export const getCodeCopyButtonMeasures = ({
    controlHeights,
    borderRadius,
    touchTargets,
    pointerType,
    radiuses,
    inset
}: CodeCopyButtonMeasuresConstantType): CodeCopyButtonMeasures => {
    return {
        margin: getCodeActionsHitMargin({
            controlHeights,
            touchTargets,
            pointerType
        }),
        borderRadius: concentric({
            role: radiuses.actions,
            outer: borderRadius,
            inset
        })
    };
};

export const getCodeActionsInset = ({
    controlHeights,
    spaces,
    size
}: CodeViewerActionsInsetConstantType): ConcentricInsetType => {
    return {
        horizontal: size === "small" ? spaces.spacingXs : spaces.spacingSm,
        vertical: (controlHeights[size] - controlHeights.small) / 2
    };
};

export const getCodeHeaderStyle = ({
    controlPaddings,
    controlHeights,
    borderRadius,
    shapeConfigs,
    borders,
    colors,
    spaces,
    size,
    type
}: CodeViewerBandStyleConstantType): ViewStyle => {
    const innerRadius = concentric({
        inset: borders.line,
        outer: borderRadius,
        role: borderRadius
    });

    const inset = getCodeActionsInset({
        controlHeights,
        spaces,
        size
    });

    const style: Mutable<ViewStyle> = {
        ...getCornersStyle({
            corners: {
                borderStartStartRadius: innerRadius,
                borderStartEndRadius: innerRadius
            },
            shapeConfigs
        }),
        borderBottomColor: type === "danger" ? colors.content.border.danger : colors.content.border.subtle,
        borderBottomWidth: borders.separator,
        paddingStart: controlPaddings.small,
        minHeight: controlHeights[size],
        paddingEnd: inset.horizontal,
        gap: spaces.spacingSm,
        paddingVertical: 0
    };

    if(type !== "danger") {
        style.backgroundColor = colors.system.state.overlay.fill.default;
    }

    return style;
};

export const getCodeFooterStyle = ({
    controlHeights,
    borderRadius,
    shapeConfigs,
    borders,
    colors,
    spaces,
    size,
    type
}: CodeViewerBandStyleConstantType): ViewStyle => {
    const inset = getCodeActionsInset({
        controlHeights,
        spaces,
        size
    });

    const style: Mutable<ViewStyle> = {
        ...getFooterBandStyle({
            surfaceBorderWidth: borders.line,
            outerRadius: borderRadius,
            inset: spaces.spacingMd,
            shapeConfigs,
            borders,
            colors,
            spaces
        }),
        minHeight: controlHeights[size],
        paddingHorizontal: undefined,
        paddingStart: spaces.spacingMd,
        paddingEnd: inset.horizontal,
        gap: spaces.spacingSm,
        paddingVertical: 0
    };

    if(type === "danger") {
        style.borderTopColor = colors.content.border.danger;
        style.backgroundColor = "transparent";
    }

    return style;
};

export const getCodeTitleStyles = ({
    shapeConfigs,
    typography,
    radiuses,
    colors,
    spaces,
    size,
    type
}: CodeViewerTitleStyleConstantType) => {
    const labelTypography = size === "small" ? typography.labelSmallSize : typography.labelMediumSize;

    const dangerTypography = size === "small" ? {
        ...typography.labelSmallSize,
        fontWeight: typography.titleSmallSize.fontWeight
    } : typography.titleSmallSize;

    const titleStyle = type === "danger" ? {
        ...dangerTypography,
        color: colors.content.text.danger
    } : {
        ...getCodeTextStyle({
            typography,
            size
        }),
        fontWeight: labelTypography.fontWeight,
        color: colors.content.text.high
    };

    return {
        languageChip: {
            backgroundColor: colors.system.container.neutral,
            paddingHorizontal: spaces.spacingSm,
            paddingVertical: spaces.spacingXxs,
            ...getCornerStyle({
                radius: radiuses.chip,
                shapeConfigs
            })
        } as Mutable<ViewStyle>,
        title: titleStyle as Mutable<TextStyle>,
        container: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>
    };
};

export const getCodeLineStateColor = ({
    lineState,
    colors
}: CodeViewerLineStateStyleConstantType): string | undefined => {
    if(lineState === "highlighted") {
        return colors.content.container.selected;
    }

    if(lineState === "added") {
        return colors.system.container.success;
    }

    if(lineState === "removed") {
        return colors.system.container.error;
    }

    return undefined;
};

export const getCodeCopyStatusDelay = ({
    durations
}: CodeCopyStatusDelayConstantType): number => {
    const [
        durationKey,
        durationScale
    ] = CODE_COPY_STATUS_DURATION;

    return durations[durationKey] * durationScale;
};

const stylesheet = StyleSheet.create({
    codeText: {
        ...webStyle({
            fontVariantLigatures: "none",
            whiteSpace: "pre"
        })
    },
    header: {
        justifyContent: "space-between",
        flexDirection: "row",
        alignItems: "center",
        borderStyle: "solid"
    },
    wrappedCodeText: {
        flexShrink: 1,
        ...webStyle({
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap"
        })
    },
    foldColumn: {
        justifyContent: "center",
        alignItems: "center",
        alignSelf: "stretch",
        flexShrink: 0
    },
    foldSummary: {
        justifyContent: "center",
        alignItems: "center",
        borderStyle: "solid",
        ...webStyle({
            userSelect: "none"
        })
    },
    line: {
        alignItems: "flex-start",
        flexDirection: "row"
    },
    languageChip: {
        justifyContent: "center",
        alignItems: "center",
        flexShrink: 0
    },
    titleIcon: {
        justifyContent: "center",
        alignItems: "center",
        flexShrink: 0
    },
    bodyContent: {
        flexDirection: "column",
        minWidth: "100%",
        flexGrow: 1
    },
    body: {
        ...Platform.select({
            default: {
                direction: "ltr"
            },
            web: {
            }
        })
    },
    container: {
        flexDirection: "column",
        borderStyle: "solid"
    },
    lineNumber: {
        textAlign: "right",
        flexShrink: 0,
        ...webStyle({
            userSelect: "none"
        })
    },
    language: {
        flexShrink: 1,
        ...webStyle({
            userSelect: "none"
        })
    },
    lineStateLabel: {
        ...webStyle({
            userSelect: "none"
        })
    },
    sign: {
        textAlign: "center",
        flexShrink: 0,
        ...webStyle({
            userSelect: "none"
        })
    },
    headerActions: {
        flexDirection: "row",
        alignItems: "center",
        marginStart: "auto",
        flexShrink: 0
    },
    footerActions: {
        flexDirection: "row",
        alignItems: "center",
        marginStart: "auto",
        flexShrink: 0
    },
    footer: {
        flexDirection: "row",
        alignItems: "center",
        borderStyle: "solid"
    },
    title: {
        flexDirection: "row",
        alignItems: "center",
        flexShrink: 1,
        flexGrow: 1,
        minWidth: 0
    },
    code: {
        flexDirection: "row",
        alignItems: "center",
        flexGrow: 1
    },
    bodyFrame: {
        overflow: "hidden",
        flexShrink: 1
    },
    wrappedCode: {
        flexShrink: 1,
        minWidth: 0
    },
    footerTitle: {
        flexShrink: 1
    },
    titleText: {
        flexShrink: 1
    },
    bodyScroll: {
        flexGrow: 0
    },
    fade: {
        bottom: 0,
        top: 0
    }
});

export const useStyles = ({
    isLineNumberVisible,
    isCollapsible,
    isSignVisible,
    borderRadius,
    shapeConfigs,
    surfaceColor,
    typography,
    iconSizes,
    maxDigits,
    radiuses,
    borders,
    colors,
    spaces,
    size,
    type
}: CodeViewerDynamicStyleType) => {
    const codeTextStyle = getCodeTextStyle({
        typography,
        size
    });

    const gutterMeasures = getCodeGutterMeasures({
        isLineNumberVisible,
        isCollapsible,
        isSignVisible,
        iconSizes,
        spaces,
        size
    });

    const numberWidth = Math.max(gutterMeasures.numberWidth, Math.ceil(maxDigits * (codeTextStyle.fontSize ?? 0) * CODE_MONO_ADVANCE) + spaces.spacingXs);

    const isDanger = type === "danger";

    const styles = {
        container: {
            backgroundColor: isDanger ? colors.system.state.overlay.fill.error : colors.content.container.raised,
            borderColor: isDanger ? colors.content.border.danger : colors.content.border.subtle,
            ...getCornerStyle({
                radius: borderRadius,
                shapeConfigs
            }),
            borderWidth: borders.line
        } as Mutable<ViewStyle>,
        errorLineText: {
            fontWeight: typography.labelMediumSize.fontWeight,
            color: colors.content.text.danger
        } as Mutable<TextStyle>,
        sign: {
            ...codeTextStyle,
            fontWeight: typography.labelMediumSize.fontWeight,
            width: gutterMeasures.signWidth
        } as Mutable<TextStyle>,
        foldSummary: {
            backgroundColor: colors.content.container.raised,
            borderColor: colors.content.border.subtle,
            paddingHorizontal: spaces.spacingXs,
            marginHorizontal: spaces.spacingXs,
            ...getCornerStyle({
                radius: radiuses.xs,
                shapeConfigs
            }),
            borderWidth: borders.line
        } as Mutable<ViewStyle>,
        code: {
            paddingStart: gutterMeasures.codePaddingStart,
            paddingEnd: gutterMeasures.codePaddingEnd
        } as Mutable<ViewStyle>,
        line: {
            minHeight: codeTextStyle.lineHeight
        } as Mutable<ViewStyle>,
        foldColumn: {
            zIndex: shapeConfigs.layers.raised,
            width: gutterMeasures.foldWidth
        } as Mutable<ViewStyle>,
        foldableLine: {
            zIndex: shapeConfigs.layers.raised
        } as Mutable<ViewStyle>,
        addedSign: {
            color: colors.content.text.success
        } as Mutable<TextStyle>,
        removedSign: {
            color: colors.content.text.danger
        } as Mutable<TextStyle>,
        bodyContent: {
            paddingVertical: spaces.spacingSm
        } as Mutable<ViewStyle>,
        lineNumber: {
            ...codeTextStyle,
            color: colors.content.text.low,
            paddingEnd: spaces.spacingXs,
            width: numberWidth
        } as Mutable<TextStyle>,
        traceLineText: {
            color: colors.content.text.mid
        } as Mutable<TextStyle>,
        fade: {
            width: spaces.spacingXl
        } as Mutable<ViewStyle>,
        codeText: {
            ...codeTextStyle,
            color: colors.code.text
        } as Mutable<TextStyle>,
        lineStateLabelContainer: {
            height: borders.line,
            width: borders.line
        } as Mutable<ViewStyle>
    };

    if(!isLineNumberVisible) {
        styles.sign.marginStart = spaces.spacingXs;
    }

    if(!isLineNumberVisible && !isSignVisible) {
        styles.foldColumn.marginStart = spaces.spacingXs;
    }

    return {
        ...styles,
        fadeColor: isDanger ? getOpaqueColor({
            surface: colors.content.container[surfaceColor],
            color: colors.system.state.overlay.fill.error
        }) : colors.content.container.raised
    };
};
export default stylesheet;
